// "Start here" for website owners (role tenant) + Admin → Settings → Weekly reports.
// Data: GET /api/onboarding (cloud) with the site the desktop is connected to (GET /api/platform, local).
import { useEffect, useState } from "preact/hooks";

type Prompt = { id: string; title: string; detail: string; prompt: string };
export type OnboardingData = {
  show: boolean;
  firstTime?: boolean;
  site?: { siteId: string; siteName: string; liveUrl: string | null; focus: string | null } | null;
  credits?: { used: number; budget: number; left: number };
  plan?: { pricePkr: number; credits: number; image: number; imagePro: number; tokenMarkup: number; socialPost: number };
  prompts?: Prompt[];
};

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`/api${path}`, { headers: { "content-type": "application/json" }, ...init });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j as T;
}
const n = (v: number) => Math.round(v).toLocaleString();

/** Loads the Start here data for website owners; `null` for staff/admins (they never see it). */
export function useOnboarding(role: string, desktop: boolean): { data: OnboardingData | null; reload: () => void } {
  const [data, setData] = useState<OnboardingData | null>(null);
  const load = async () => {
    if (role !== "tenant") return setData(null);
    let q = "";
    if (desktop) {
      const st = await getJson<any>("/platform").catch(() => null);
      const s = st?.connected && st.sites?.[0];
      if (s?.site_id) q = `?site_id=${encodeURIComponent(s.site_id)}&site_name=${encodeURIComponent(s.site_name ?? "")}`;
    }
    setData(await getJson<OnboardingData>(`/onboarding${q}`).catch(() => null));
  };
  useEffect(() => {
    load();
  }, [role]);
  return { data, reload: load };
}

const CSS = `
.ob{overflow:auto;padding:28px 26px 40px}
.ob__in{max-width:980px;margin:0 auto}
.ob__eyebrow{color:var(--accent2);font-size:12px;letter-spacing:.14em;text-transform:uppercase;font-weight:600}
.ob h2{font-size:26px;margin:6px 0 4px;line-height:1.2}
.ob__lead{color:var(--muted);margin:0 0 20px;max-width:640px}
.ob__site{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:24px}
.ob__card{background:var(--bg2);border:1px solid var(--line);border-radius:14px;padding:14px 16px;min-width:200px;flex:1}
.ob__card b{display:block;font-size:18px;margin-top:2px;word-break:break-word}
.ob__card span{color:var(--muted);font-size:12px}
.ob__meter{height:6px;border-radius:99px;background:var(--bg3);margin-top:8px;overflow:hidden}.ob__meter i{display:block;height:100%;background:var(--accent)}
.ob h3{margin:26px 0 10px;font-size:15px}
.ob__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:10px}
.ob__prompt{all:unset;box-sizing:border-box;cursor:pointer;background:var(--bg2);border:1px solid var(--line);border-radius:14px;padding:14px 16px;display:flex;flex-direction:column;gap:4px;min-height:92px;transition:border-color .15s,transform .15s}
.ob__prompt:hover,.ob__prompt:focus-visible{border-color:var(--accent);transform:translateY(-1px)}
.ob__prompt b{font-size:14px}.ob__prompt span{color:var(--muted);font-size:12.5px}
.ob__prompt em{margin-top:auto;color:var(--accent2);font-style:normal;font-size:12px}
.ob__help{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:10px}
.ob__help .ob__card ul{margin:8px 0 0;padding-left:18px;color:var(--fg);font-size:13px}.ob__help .ob__card li{margin:3px 0}
.ob__help .ob__card p{margin:6px 0 0;font-size:13px;color:var(--fg)}
.ob__foot{display:flex;gap:10px;align-items:center;margin-top:24px;flex-wrap:wrap}
@media (max-width:640px){.ob{padding:20px 16px 32px}.ob h2{font-size:22px}.ob__prompt{min-height:0}}
`;

export function Onboarding({ data, name, onStart, onClose }: { data: OnboardingData; name: string; onStart: (prompt: string) => void; onClose: () => void }) {
  const site = data.site;
  const c = data.credits ?? { used: 0, budget: 0, left: 0 };
  const p = data.plan ?? { pricePkr: 3000, credits: 3000, image: 2, imagePro: 4, tokenMarkup: 2, socialPost: 2 };
  const pct = c.budget ? Math.min(100, (c.left / c.budget) * 100) : 0;
  return (
    <div class="ob">
      <style>{CSS}</style>
      <div class="ob__in">
        <div class="ob__eyebrow">Start here</div>
        <h2>Welcome, {name.split(" ")[0]}. Your agent is ready to work on {site?.siteName ?? "your website"}.</h2>
        <p class="ob__lead">Click any task below and the agent starts on it straight away. It shows you a preview and asks before anything goes live on your website.</p>
        <div class="ob__site">
          <div class="ob__card">
            <span>Your website</span>
            <b>{site?.siteName ?? "Not connected yet"}</b>
            {site?.liveUrl ? <a href={site.liveUrl} target="_blank" rel="noopener">{site.liveUrl.replace(/^https?:\/\//, "")} ↗</a> : <span>{site ? "Live address not set up yet" : "Sign out and in again with your Xelent Sites account"}</span>}
          </div>
          <div class="ob__card">
            <span>Credits left this month</span>
            <b>{n(c.left)} of {n(c.budget)}</b>
            <div class="ob__meter"><i style={`width:${pct}%`} /></div>
          </div>
        </div>

        <h3>Try one of these</h3>
        <div class="ob__grid">
          {(data.prompts ?? []).map((x) => (
            <button class="ob__prompt" onClick={() => onStart(x.prompt)} title={x.prompt}>
              <b>{x.title}</b>
              <span>{x.detail}</span>
              <em>Start →</em>
            </button>
          ))}
        </div>

        <h3>Good to know</h3>
        <div class="ob__help">
          <div class="ob__card">
            <b style="font-size:14px">How credits work</b>
            <ul>
              <li>{n(p.credits)} credits a month (PKR {n(p.pricePkr)}), renewed on the 1st</li>
              <li>{p.image} credits per image, {p.imagePro} per pro image</li>
              <li>Writing and edits: tokens at {p.tokenMarkup}× cost (a typical edit is a few credits)</li>
              <li>{p.socialPost} credits per scheduled social post</li>
              <li>The cost of every task shows under it in the chat</li>
            </ul>
          </div>
          <div class="ob__card">
            <b style="font-size:14px">Approvals</b>
            <p>The agent drafts changes and shows you a preview link. Nothing is published until you say yes in the chat ("yes, publish it"). Ask "undo the last change" to roll back.</p>
          </div>
          <div class="ob__card">
            <b style="font-size:14px">Scheduled publishing</b>
            <p>Say when it should go live: "publish this blog post on Monday at 10 am". The agent schedules it and it publishes on time, even when this computer is off. Ask "what is scheduled?" to see the list.</p>
          </div>
        </div>
        <div class="ob__foot">
          <button class="btn primary" onClick={onClose}>Go to my chats</button>
          <span class="hint">You can come back here any time from "Start here" in the sidebar.</span>
        </div>
      </div>
    </div>
  );
}

// ---------------- Admin → Settings → Weekly reports

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function WeeklyReportsAdmin() {
  const [d, setD] = useState<any>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [r, s] = await Promise.all([getJson<any>("/admin/weekly-reports"), getJson<any>("/admin/settings")]);
    setD(r);
    const v = (k: string) => s.settings[k]?.value ?? "";
    setForm({ weekly_report_enabled: v("weekly_report_enabled"), weekly_report_day: v("weekly_report_day"), weekly_report_hour: v("weekly_report_hour"), weekly_report_owner_email: v("weekly_report_owner_email"), weekly_report_owner_copy: v("weekly_report_owner_copy") });
  };
  useEffect(() => {
    load().catch((e) => setMsg(e.message));
  }, []);
  if (!d) return <div class="hint" style="margin-top:28px">{msg || "Loading weekly reports…"}</div>;
  const set = (k: string, v: string) => setForm({ ...form, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await getJson("/admin/settings", { method: "PUT", body: JSON.stringify(form) });
      await load();
      setMsg("Saved");
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    setMsg("Sending…");
    try {
      const r = await getJson<any>("/admin/weekly-reports/test", { method: "POST", body: JSON.stringify({}) });
      setMsg(`Test report sent to ${r.to}${r.tenant ? ` (figures of ${r.tenant.name})` : " (sample figures)"}`);
      load();
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style="margin-top:28px;border-top:1px solid var(--line);padding-top:16px">
      <h3 style="margin:0 0 6px">Weekly reports to website owners</h3>
      <p class="hint" style="max-width:720px">Every week each active website owner gets an email: credits used and left, tasks run, images made, blog posts and website updates published, social posts scheduled, and three suggested next steps. Sent from {d.from || "support@ayzelify.com"}. {d.emailReady ? "" : "Email is not connected on this server yet."}</p>
      <div class="form">
        <div class="field"><label>Weekly reports</label><select class="input" value={form.weekly_report_enabled} onChange={(e) => set("weekly_report_enabled", (e.target as HTMLSelectElement).value)}><option value="0">Off</option><option value="1">On</option></select></div>
        <div class="field"><label>Send on (Pakistan time)</label><select class="input" value={form.weekly_report_day} onChange={(e) => set("weekly_report_day", (e.target as HTMLSelectElement).value)}>{DAYS.map((x, i) => <option value={String(i)}>{x}</option>)}</select></div>
        <div class="field"><label>At</label><select class="input" value={form.weekly_report_hour} onChange={(e) => set("weekly_report_hour", (e.target as HTMLSelectElement).value)}>{Array.from({ length: 24 }, (_, h) => <option value={String(h)}>{`${String(h).padStart(2, "0")}:00`}</option>)}</select></div>
        <div class="field"><label>Owner's email (copies + test reports)</label><input class="input" type="email" value={form.weekly_report_owner_email} placeholder="blank = your own sign-in email" onInput={(e) => set("weekly_report_owner_email", (e.target as HTMLInputElement).value)} /></div>
        <div class="field"><label>Send the owner a copy of every report</label><select class="input" value={form.weekly_report_owner_copy} onChange={(e) => set("weekly_report_owner_copy", (e.target as HTMLSelectElement).value)}><option value="0">No</option><option value="1">Yes</option></select></div>
      </div>
      <div style="margin-top:12px;display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <button class="btn primary small" onClick={save} disabled={busy}>Save</button>
        <button class="btn small" onClick={test} disabled={busy}>Send test report to me</button>
        <a class="btn small" href="/api/admin/weekly-reports/preview" target="_blank" rel="noopener">Preview</a>
        <span class="hint">{msg || (d.enabled && d.nextRun ? `Next send ${new Date(d.nextRun).toLocaleString()}` : "Off: no reports are sent")}</span>
      </div>
      <p class="hint" style="margin-top:12px">Website owners: {d.tenants.filter((t: any) => t.active).length} active{d.tenants.length ? ` · ${d.tenants.filter((t: any) => t.active).map((t: any) => `${t.name}${t.site_name ? ` (${t.site_name})` : ""}`).join(", ")}` : ""}</p>
      {d.recent.length > 0 && (
        <table class="grid" style="margin-top:8px">
          <thead><tr><th>When</th><th>Owner</th><th>Week</th><th>Kind</th><th>To</th><th>Status</th></tr></thead>
          <tbody>{d.recent.slice(0, 15).map((r: any) => <tr><td>{new Date(r.created_at).toLocaleString()}</td><td>{r.staff_name ?? (r.staff_id === "sample" ? "sample" : r.staff_id)}</td><td>{String(r.week).replace(/^test-.*/, "test")}</td><td>{r.kind}</td><td>{r.to_email}</td><td title={r.error ?? ""}>{r.status}{r.error ? ` · ${r.error.slice(0, 60)}` : ""}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}
